import GoogletagFacade from '../../googletagFacade/GoogletagFacade'
import { queryParamHas } from '../../helpers/getQueryParam'
import Logger from '../../helpers/Logger'
import { TCF } from '../tcf/TCF'
import WebModel from '../../models/WebModel'

const GOOGLE_VENDOR_ID = 755
const TCF_API_VERSION = 2

export class GDPR implements IGDPR {
  public enableGdpr: boolean = false
  private isEU: boolean
  private gdprCompliance: boolean
  private readyCallbackStack: Array<Function> = []
  private gdprDone = false

  constructor(model: WebModel) {
    const { gdpr, gdpr_compliance } = model
    const isDisableCmp = queryParamHas('test', 'disableGdpr')
    const isGdprTest = queryParamHas('test', 'gdpr')

    this.isEU = gdpr === '1'
    this.gdprCompliance = !!gdpr_compliance
    this.enableGdpr =
      (this.isEU && this.gdprCompliance) || isDisableCmp || isGdprTest
  }

  /**
   * Initializes the GDPR Preact modal and privacy link
   */
  public async initModal(): Promise<any> {
    if (this.enableGdpr) {
      try {
        await this.loadAssets()
      } catch (error) {
        const errorMessage = 'GDPR: Error loading "gdprModal" webpackChunk'
        Logger.custom(['debug', 'gpdr'], errorMessage)
        window.$mediavine.newrelic.addPageAction('gdprError', { error, errorMessage })
      }
      this.setupCmp() 
    } else {
      TCF.consentDoesNotApply()
      if (this.isEU && !this.gdprCompliance) {
        try {
          await this.disablePersonalizedAds()
        } catch (error) {
          const errorMessage = 'GDPR: Error setting non-personalized ads'
          Logger.custom(['debug', 'gpdr'], errorMessage)
          window.$mediavine.newrelic.addPageAction('gdprError', { error, errorMessage })
        }
      }
      this.isReady()
    }
  }

  /** Handles GDPR onReady callback listeners */
  public onReady(callback: Function): void {
    /** We're already done, fire the callback */
    if (this.gdprDone) {
      return callback()
    }

    /** We need to wait for the CMP to be accepted or loaded */
    if (this.enableGdpr) {
      this.readyCallbackStack.push(callback)
      return
    }

    /** GDPR does not apply; we're "ready", execute callbacks immediately */
    callback()
  }

  /** Handles applicable onReady callback listeners */
  private isReady(): void {
    const data = TCF.tcModel || {}
    window.$mediavine.newrelic.setCustomAttribute(
      'PurposeConsents',
      data.purposeConsents?.size
    )
    window.$mediavine.newrelic.setCustomAttribute(
      'PurposeLegitimateInterests',
      data.purposeLegitimateInterests?.size
    )

    if (this.enableGdpr) {
      let func = this.readyCallbackStack.pop()
      while (func) {
        func()
        func = this.readyCallbackStack.pop()
      }
    }
  }

  /** Disables all personalized ads */
  private disablePersonalizedAds(): Promise<any> {
    return GoogletagFacade.onReady(() => {
      window.googletag.pubads().setRequestNonPersonalizedAds(1)
      Logger.debug('GDPR: Non-personalized ads has been set')
    })
  }

  /** Dynamically imports GDPR Preact modal */
  private loadAssets(): Promise<any> {
    return import(/* webpackChunkName: "gdprModal" */ './assets/App')
  }

  /** Add CmpApi event handlers */
  private setupCmp(): void {
    window.__tcfapi(
      'addEventListener',
      TCF_API_VERSION,
      this.tcfApiEventHandler.bind(this)
    )
  }

  /** Handles tcData eventStatuses */
  private tcfApiEventHandler(tcData: TcData, success: boolean): void {
    switch (success && tcData.eventStatus) {
      case TcDataEventStatus.tcloaded: {
        window.$mediavine.newrelic.addPageAction('gdprModal', { state: 'CMP Previously Accepted' })
        Logger.debug('GDPR: TFC TC Loaded')
        this.gdprDone = true
        this.isReady()
        break
      }
      case TcDataEventStatus.cmpuishown: {
        window.$mediavine.newrelic.addPageAction('gdprModal', { state: 'CMP Displayed' })
        Logger.debug('GDPR: TCF GDPR UI Shown')
        break
      }
      case TcDataEventStatus.useractioncomplete: {
        Logger.debug('GDPR: TCF GDPR user action complete')
        window.$mediavine.newrelic.addPageAction('gdprModal', { state: 'CMP Accepted' })
        this.gdprDone = true
        this.isReady()
        break
      }
    }
  }
}

enum TcDataEventStatus {
  tcloaded = 'tcloaded',
  cmpuishown = 'cmpuishown',
  useractioncomplete = 'useractioncomplete'
}

interface TcData {
  tcString: string
  tcfPolicyVersion: number
  cmpId: number
  cmpVersion: number
  gdprApplies: boolean | undefined
  eventStatus: string
  cmpStatus: string
  listenerId: number | undefined
  isServiceSpecific: boolean
  useNonStandardStacks: boolean
  publisherCC: string
  purposeOneTreatment?: boolean
  outOfBand?: {
    allowedVendors: {
      [key: string]: boolean
    }
    disclosedVendors: {
      [key: string]: boolean
    }
  }
  purpose: {
    consents: {
      [key: string]: boolean
    }
    legitimateInterests: {
      [key: string]: boolean
    }
  }
  vendor: {
    consents: {
      [key: string]: boolean
    }
    legitimateInterests: {
      [key: string]: boolean
    }
  }
  specialFeatureOptins: {
    [key: string]: boolean
  }
  publisher: {
    consents: {
      [key: string]: boolean
    }
    legitimateInterests: {
      [key: string]: boolean
    }
    customPurpose: {
      consents: {
        [key: string]: boolean
      }
      legitimateInterests: {
        [key: string]: boolean
      }
    }
    restrictions: {
      [key: string]: {
        [key: string]: number
      }
    }
  }
}
